import { useFirestore, useFirestoreDocData, useFunctions } from "reactfire";
import { collection, doc, deleteDoc, updateDoc } from "firebase/firestore";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BreadcrumbItem, Breadcrumbs, Button, ButtonGroup, Card, CardBody, Code, Kbd, Tab, Tabs, Textarea } from "@nextui-org/react";
import { FileUpload } from "../../components/fileUpload";
import { Block, DoxType, FileType } from "../../types/types";
import BlockEditor from "../../components/blockEditor";
import TiffViewer from "../../components/tiffViewer";
import { useEffect, useState } from "react";
import { AiOutlinePlaySquare } from "react-icons/ai";
import { httpsCallable } from "firebase/functions";
import { PDFViewer } from "../../components/pdfViewer";
import { MonitorDashboard } from "../common/monitor";

export const Generator = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const functions = useFunctions();

    const firestore = useFirestore();
    const { data: generator } = useFirestoreDocData(doc(firestore, 'generators/' + id), { idField: 'id' });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [error, setError] = useState<any>();
    const [newBlocks, setNewBlocks] = useState<Block[]>([]);
    const [newInstruction, setNewInstruction] = useState<string>();
    const [newRails, setNewRails] = useState<string>();

    const saveGenerator = () => {
        //save document in firestore 
        updateDoc(doc(collection(firestore, 'generators'), id), {
            instruction: newInstruction,
            blocks: newBlocks,
            rails: newRails ? newRails : "",
        });
    }


    const deleteGenerator = () => {
        //delete document in firestore
        deleteDoc(doc(firestore, 'generators/' + id));
        navigate(`/`)
    }


    const handleUpload = (newUploadFile: FileType) => {

        console.log('handleUpload + ', newUploadFile)

        updateDoc(doc(collection(firestore, 'generators'), id), {
            testFile: {
                ...newUploadFile,
            }
        });

        console.log('upload');
    }
    const handleDelete = () => {
        updateDoc(doc(collection(firestore, 'generators'), id), {
            testFile: null
        });
        console.log('delete');
    }

    useEffect(() => {

        if (generator) {
            setNewBlocks(generator.blocks);
            setNewInstruction(generator.instruction);
            setNewRails(generator.rails);
        }

    }, [generator]);

    if (!generator) {
        return (
            <div className="flex w-full h-full justify-center items-center">
                <div className="text-2xl">Loading...</div>
            </div>
        )
    }

    const callTestRun = async () => {
        try {
            if (isProcessing) return;

            setIsProcessing(true);
            setError('');


            var fnName = 'api/testgenerate/' + generator.id;
            const callableFunction = httpsCallable(functions, fnName);
            const result = await callableFunction();

            console.log(result);
        } catch (err) {
            setError(err);
            console.log(err);
        }
        setIsProcessing(false);
    };

    const _showTestFile = (file: FileType) => {
        if (file.name.toLowerCase().endsWith("pdf")) {
            return <PDFViewer filePath={file.path} />
        }
        if (file.name.toLowerCase().endsWith("tiff") || file.name.toLowerCase().endsWith("*.tif")) {
            return <TiffViewer filePath={file.path} />
        }
        return <></>
    }

    return (
        <div className="">
            <div className="flex flex-row items-end justify-between">
                <div className=" ">
                    <Breadcrumbs>
                        <BreadcrumbItem><Link to="/generators">Home</Link></BreadcrumbItem>
                        <BreadcrumbItem>Generator</BreadcrumbItem>
                    </Breadcrumbs>
                    <div className="text-2xl mt-4 font-bold">{generator?.name} <Code size="sm">{id}</Code></div>
                    <div className="text-md">{generator?.description} </div>
                </div>
                <div className="px-4 align-bottom flex-col gap-4">

                    <div className="flex">
                        <ButtonGroup>
                            <Button color="primary" aria-label="Save" onClick={saveGenerator}>
                                <Kbd keys={["command"]}>S</Kbd>Save
                            </Button>
                            <Button color="danger" aria-label="Delete" onClick={deleteGenerator}>
                                Delete
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>
            </div>
            <div className="flex w-full flex-col mt-8">
                <Tabs aria-label="Dynamic tabs" >
                    <Tab key="Develop" title="Develop">
                        <Card>
                            <CardBody>

                                <div className="flex w-full min-h-[70vh] max-h-[70vh]">

                                    <div className="flex columns-3 gap-5 w-full">
                                        <div className="flex-1 overflow-y-auto p-4 bg-base-100  ">
                                            <div className="mt-2">
                                                <div className="text-lg font-bold">Sample Input</div>
                                                <span className="text-sm">Input file for Testing and Validation </span>
                                            </div>
                                            <div className="flex mt-5 justify-between bg-green-50 p-4  rounded-lg">
                                                <FileUpload fileObj={generator?.testFile} handleUpload={handleUpload} handleDelete={handleDelete} />
                                            </div>
                                            {generator && generator.testFile ? (
                                                _showTestFile(generator.testFile)
                                            ) : <></>}
                                        </div>
                                        <div className="flex-1 overflow-y-auto p-4 bg-slate-100 ">
                                            <div className="mt-2">
                                                <div className="text-lg font-bold">1. Editor</div>
                                                <span className="text-sm">Describe your Input document </span>
                                            </div>
                                            <div className="mt-2 flex flex-col">
                                                <Textarea
                                                    label="Describe the input document"
                                                    isRequired
                                                    variant="bordered"
                                                    value={newInstruction}
                                                    onChange={(e) => { setNewInstruction(e.target.value) }}
                                                />
                                                <div className="mt-2">
                                                    <div className="text-lg font-bold">2. Blocks</div>
                                                    <span className="text-sm">Edit your output template </span>
                                                </div>

                                                <BlockEditor baseBlocks={newBlocks} onUpdate={(blocks) => { setNewBlocks(blocks) }} />
                                                <div className="mt-2 mb-2">
                                                    <div className="text-lg font-bold">3. DoxRails</div>
                                                    <span className="text-sm">Validate your output</span>
                                                </div>
                                                <Textarea
                                                    label="How would you know the outcome is right?"
                                                    isRequired
                                                    variant="bordered"
                                                    value={newRails}
                                                    onChange={(e) => { setNewRails(e.target.value) }}
                                                />
                                            </div>


                                        </div>
                                        <div className="flex-1 overflow-y-auto p-4 bg-base-100  ">
                                            <div className="mt-2">
                                                <div className="text-lg font-bold">Generated Output</div>
                                                <span className="text-sm">Preview of output. </span>
                                                <span className="text-sm">Each preview will trigger use of your credits</span>
                                                <div className="text-lg mt-4">
                                                    <Button className="bg-green-500" onClick={callTestRun}>
                                                        Generate <AiOutlinePlaySquare />
                                                    </Button>
                                                </div>
                                            </div>
                                            {!isProcessing ? (
                                                <div className="mt-2">
                                                    <Tabs key="preview" variant="underlined">
                                                        <Tab key="output" title="Output">
                                                            <PDFViewer filePath={generator.outputFile?.path} />
                                                        </Tab>
                                                        <Tab key="doxrails" title="DoxRails">
                                                            <PDFViewer filePath={generator.doxRailsFile?.path} />
                                                        </Tab>

                                                    </Tabs>
                                                </div>
                                            ) :
                                                (
                                                    //loading screen 
                                                    <div className="flex justify-center items-center h-full">
                                                        <div className="flex flex-col items-center">
                                                            <div className="text-2xl">Generating...</div>
                                                            <div className="text-md">This may take a few minutes</div>
                                                            <div className="text-md">You will be charged for the number of credits used</div>
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Tab>
                    <Tab key="Publish" title="Publish">
                        <Card>
                            <CardBody>

                            </CardBody>
                        </Card>
                    </Tab>
                    <Tab key="Monitor" title="Monitor">
                        <Card>
                            <CardBody>
                                <MonitorDashboard dox={generator as DoxType} />
                            </CardBody>
                        </Card>
                    </Tab>
                </Tabs>
            </div>
        </div>
    );
}